import { SubmitSuccessAction, UrlTargetEnumTarget } from '@wix/ambassador-forms-v4-form/types';
import { useCallback, useEffect, useState } from 'react';
import { useFieldActions } from '../../../contexts/field-actions-context';
import { useForm } from '../../../contexts/form-context';
import { getCurrentStep, getErrorCode, isValidUrl } from './utils';
import { ExternalUrlTarget, SubmitAction } from '../submit-button-types';
import { useSubmitErrorMessage } from './use-submit-error';
import { getCheckoutMessage } from './checkout-message';
import { useTranslation } from '../../../contexts/translation-context';
import { useExperiments } from '../../../contexts/experiments-context';
import { EXPERIMENTS } from '../../../constants';
import { FORM_STATUS, useFormStatus } from '../../../contexts/form-status-provider';
export const useSubmitButton = _ref => {
  let {
    fieldId,
    forceThankYouMessage,
    text,
    submitText,
    ...props
  } = _ref;
  const form = useForm();
  const {
    t
  } = useTranslation();
  const {
    isExperimentEnabled
  } = useExperiments();
  const useSubmitStatusMessage = isExperimentEnabled(EXPERIMENTS.SUBMIT_STATUS_MESSAGE_VIEWER);
  const {
    thankYouMessageDuration,
    thankYouMessageText,
    externalUrl,
    externalUrlTarget,
    submitAction
  } = form.submitSettings ? transformSubmitSettings(form.submitSettings) : props;
  const {
    hasSteps,
    isLastStep
  } = getCurrentStep(form, fieldId);
  const {
    inProgress,
    actions
  } = useFieldActions();
  const [showMessage, setShowMessage] = useState(false);
  const [showCheckoutMessage, setShowCheckoutMessage] = useState(false);
  const [errorMessageCode, setErrorMessageCode] = useState();
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const errorMessage = useSubmitErrorMessage(errorMessageCode);
  const forceInProgress = showCheckoutMessage;
  const thankYouMessage = showCheckoutMessage ? getCheckoutMessage(t('submit.checkout.message')) : thankYouMessageText;
  const {
    formStatus
  } = useFormStatus();
  const submitButtonText = submitText || text || '';
  const shouldShowMessage = submitAction === SubmitAction.THANK_YOU_MESSAGE && !!thankYouMessageText;
  const isThankYouMessageVisible = (!hasSteps || isLastStep) && (showMessage || forceThankYouMessage || showCheckoutMessage) && !useSubmitStatusMessage;
  const shouldRedirect = submitAction === SubmitAction.EXTERNAL_URL && externalUrl && isValidUrl(externalUrl);
  const isDisabled = submitInProgress;
  const isInProgress = inProgress || forceInProgress || formStatus === FORM_STATUS.BUSY;
  useEffect(() => {
    if (showMessage && thankYouMessageDuration) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, thankYouMessageDuration * 1000);
      return () => clearTimeout(timer);
    }
    return;
  }, [showMessage, thankYouMessageDuration]);
  const handleRedirect = useCallback(() => {
    const urlWithoutProtocol = externalUrl == null ? void 0 : externalUrl.replace(/^https?:\/\//, '');
    const updatedExternalUrl = `https://${urlWithoutProtocol}`;
    window.open(updatedExternalUrl, externalUrlTarget || '_blank');
  }, [externalUrl, externalUrlTarget]);
  const handleSubmitClick = useCallback(async () => {
    if (isInProgress) {
      return;
    }
    setSubmitInProgress(true);
    try {
      var _submission$orderDeta;
      const submission = await (actions == null ? void 0 : actions.submitForm());
      const checkoutId = submission == null || (_submission$orderDeta = submission.orderDetails) == null ? void 0 : _submission$orderDeta.checkoutId;
      if (checkoutId && actions != null && actions.navigateToCheckout) {
        setShowCheckoutMessage(true);
        actions == null || actions.navigateToCheckout(checkoutId);
        return;
      }
      if (submission && shouldShowMessage) {
        setShowMessage(true);
      } else if (submission && shouldRedirect) {
        handleRedirect();
      }
      setErrorMessageCode('');
    } catch (error) {
      setErrorMessageCode(getErrorCode(error));
    }
    setSubmitInProgress(false);
  }, [actions, handleRedirect, isInProgress, shouldRedirect, shouldShowMessage]);
  const handleSubmitClickWithSubmitStatusMessage = useCallback(async () => {
    if (isInProgress) {
      return;
    }
    setSubmitInProgress(true);
    try {
      await (actions == null ? void 0 : actions.submitForm());
    } catch (error) {
      console.error(error);
    }
    setSubmitInProgress(false);
  }, [actions, isInProgress]);
  return {
    handleSubmitClick: useSubmitStatusMessage ? handleSubmitClickWithSubmitStatusMessage : handleSubmitClick,
    isDisabled,
    isInProgress,
    isThankYouMessageVisible,
    thankYouMessage,
    errorMessage,
    submitButtonText
  };
};
const SUBMIT_SETTINGS_ACTION_MAP = {
  [SubmitSuccessAction.THANK_YOU_MESSAGE]: SubmitAction.THANK_YOU_MESSAGE,
  [SubmitSuccessAction.REDIRECT]: SubmitAction.EXTERNAL_URL,
  [SubmitSuccessAction.NO_ACTION]: undefined,
  [SubmitSuccessAction.UNKNOWN_SUBMIT_SUCCESS_ACTION]: undefined
};
const transformSubmitSettings = submitSettings => {
  var _submitSettings$thank, _submitSettings$thank2, _submitSettings$redir, _submitSettings$redir2;
  return {
    thankYouMessageDuration: submitSettings == null || (_submitSettings$thank = submitSettings.thankYouMessageOptions) == null ? void 0 : _submitSettings$thank.durationInSeconds,
    thankYouMessageText: submitSettings == null || (_submitSettings$thank2 = submitSettings.thankYouMessageOptions) == null ? void 0 : _submitSettings$thank2.richContent,
    externalUrl: submitSettings == null || (_submitSettings$redir = submitSettings.redirectOptions) == null ? void 0 : _submitSettings$redir.redirectUrl,
    externalUrlTarget: (submitSettings == null || (_submitSettings$redir2 = submitSettings.redirectOptions) == null ? void 0 : _submitSettings$redir2.target) === UrlTargetEnumTarget.SELF ? ExternalUrlTarget.SELF : ExternalUrlTarget.BLANK,
    submitAction: SUBMIT_SETTINGS_ACTION_MAP[submitSettings == null ? void 0 : submitSettings.submitSuccessAction]
  };
};
//# sourceMappingURL=use-submit-button.js.map